import React, { useState, useEffect } from 'react';
import { Modal, Button, Label, Alert } from 'flowbite-react';
import { HiTag, HiPlus } from 'react-icons/hi';
import { useRoleTagSuggestions } from '../../hooks/useRoleTagSuggestions';
import { SearchableSelect } from '../common/SearchableSelect';

interface AddRoleTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: number;
  organizationName: string;
  availableRoles?: Array<{ value: number; label: string }>;  // Available role descriptors
  // The person's position title at this organization, used to suggest roles
  positionTitle?: string | null;
  // Roles the person already holds here, left out of the suggestions
  existingRoleIds?: number[];
  onSave: (roleData: {
    roleDescriptorId?: number | null;
    roleName?: string | null;
  }) => Promise<void>;
}

export const AddRoleTagModal: React.FC<AddRoleTagModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  organizationName,
  availableRoles = [],
  positionTitle,
  existingRoleIds = [],
  onSave
}) => {
  const [roleDescriptorId, setRoleDescriptorId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { suggestions } = useRoleTagSuggestions(positionTitle || undefined, organizationId, isOpen);
  const availableIds = new Set(availableRoles.map(r => r.value));
  const pendingSuggestions = suggestions.filter(
    s => availableIds.has(s.descriptorId) && !existingRoleIds.includes(s.descriptorId)
  );

  useEffect(() => {
    // Reset form when modal opens
    if (isOpen) {
      setRoleDescriptorId(null);
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (!roleDescriptorId) {
      setError('Please select a role');
      setLoading(false);
      return;
    }

    try {
      await onSave({
        roleDescriptorId,
        roleName: null
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add role tag');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={isOpen} onClose={onClose} dismissible size="md">
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <HiTag className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900">
            Add Role Tag
          </h3>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Adding role tag for: <span className="font-semibold">{organizationName}</span>
            {positionTitle && (
              <span className="block text-gray-500">Position: {positionTitle}</span>
            )}
          </p>
        </div>

        {error && (
          <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {pendingSuggestions.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500">Suggested from title:</span>
              {pendingSuggestions.map(s => (
                <button
                  key={s.descriptorId}
                  type="button"
                  onClick={() => setRoleDescriptorId(s.descriptorId)}
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs ${
                    roleDescriptorId === s.descriptorId
                      ? 'border-indigo-500 bg-indigo-100 text-indigo-800'
                      : 'border-dashed border-indigo-300 text-indigo-700 hover:bg-indigo-50'
                  }`}
                >
                  <HiPlus className="h-3 w-3" />
                  {s.shortDescription}
                </button>
              ))}
            </div>
          )}

          {/* Role Selection */}
          <div>
            <Label htmlFor="roleDescriptor" className="block text-sm font-medium text-gray-700 mb-2">
              Role Type
            </Label>
            <SearchableSelect
              id="roleDescriptor"
              options={[...availableRoles].sort((a, b) => a.label.localeCompare(b.label))}
              value={roleDescriptorId}
              onChange={setRoleDescriptorId}
              placeholder="Search roles..."
              disabled={loading}
            />
          </div>

          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              Role tags are standard roles that let people find this contact with the Role filter
              in Search Contacts, whatever the local title. Need a role that isn't listed? Ask a super
              admin to add it in Admin &gt; Descriptor Management.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button color="gray" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Adding...' : 'Add Role Tag'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
